import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { RATE_SOURCES } from '@subs/shared';

import { moneyAccounts, payments, type PaymentEvent, type TransactionInput } from '../api/client';
import type { Transaction } from '../api/types';
import { deriveConversionRate } from '../lib/conversion-leg';
import { useToast } from './Toast';
import { Badge, Button, Field, Input, Modal, Select } from './ui';
import { formatAmount, formatDateTime } from '../lib/format';

/** `<input type="datetime-local">` value (local wall clock) for an ISO stamp. */
function toLocalInput(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (value: number) => String(value).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

interface LegState {
  occurredAt: string;
  amount: string;
  currency: string;
  accountId: string;
  fxRate: string;
  rateSource: (typeof RATE_SOURCES)[number];
  commission: string;
  sourceTxnNo: string;
}

const EMPTY: LegState = {
  occurredAt: toLocalInput(new Date().toISOString()),
  amount: '',
  currency: 'RUB',
  accountId: '',
  fxRate: '',
  rateSource: 'MANUAL',
  commission: '',
  sourceTxnNo: '',
};

const num = (value: string): number | null => {
  if (value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? null : parsed;
};

/**
 * The ₽ half of a payment event: one compact modal that appends a `CONVERSION`
 * leg (`POST /payments/:id/transactions`), which is what makes `rubTotal`
 * non-null for a flow-B payment recorded with its payment leg only.
 *
 * The rate works in both directions — typing the rubles derives the rate, typing
 * the rate derives the rubles (`deriveConversionRate` in `lib/conversion-leg`).
 * The same modal, opened for a stored leg, re-points its **account** only: the
 * amounts, dates, rates and bills of a leg are facts, not editable opinions.
 */
export function ConversionLegModal({
  event,
  leg = null,
  onClose,
  onSaved,
}: {
  event: PaymentEvent;
  /** Present in edit mode (account only), null in create mode. */
  leg?: Transaction | null;
  onClose: () => void;
  onSaved: (saved: PaymentEvent) => void;
}) {
  const editing = Boolean(leg);
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [state, setState] = useState<LegState>(() => ({
    ...EMPTY,
    accountId: leg?.accountId ?? '',
  }));
  const [touched, setTouched] = useState(false);

  const set = <K extends keyof LegState>(field: K, value: LegState[K]) =>
    setState((current) => ({ ...current, [field]: value }));

  const accountList = useQuery({
    queryKey: ['money-accounts', 'conversion-leg'],
    queryFn: ({ signal }) => moneyAccounts.list({ page: 1, limit: 200 }, signal),
    staleTime: 60_000,
  });

  const save = useMutation({
    mutationFn: async () => {
      if (editing && leg) {
        await payments.updateTransaction(leg.id, { accountId: state.accountId });
        // The patch returns the leg, the drawer needs the whole event.
        return payments.get(event.id);
      }

      const input: TransactionInput = {
        type: 'CONVERSION',
        occurredAt: new Date(state.occurredAt).toISOString(),
        amount: Number(state.amount),
        currency: state.currency.trim().toUpperCase() || 'RUB',
      };
      if (state.accountId) input.accountId = state.accountId;
      const derivation = deriveConversionRate({
        paidAmount: event.paidAmount,
        paidCurrency: event.paidCurrency,
        amount: num(state.amount),
        fxRate: num(state.fxRate),
      });
      if (derivation.fxRate !== null) {
        input.fxRate = derivation.fxRate;
        input.rateSource = state.rateSource;
      }
      if (derivation.rubAmount !== null) input.rubAmount = derivation.rubAmount;
      const commission = num(state.commission);
      if (commission !== null) input.commission = commission;
      if (state.sourceTxnNo.trim()) input.sourceTxnNo = state.sourceTxnNo.trim();

      return payments.addTransaction(event.id, input);
    },
    onSuccess: (saved) => {
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      queryClient.invalidateQueries({ queryKey: ['reports'] });
      toast(editing ? 'Leg account updated' : 'Conversion leg added — rubles derived', 'success');
      onSaved(saved);
    },
    onError: (error: Error) => toast(error.message, 'error'),
  });

  const errors: Record<string, string> = {};
  if (!editing) {
    if (!state.occurredAt) errors.occurredAt = 'When did the conversion happen?';
    if (num(state.amount) === null || Number(state.amount) <= 0) errors.amount = 'Amount above zero is required';
    if (state.currency.trim().length !== 3) errors.currency = '3-letter code';
  }
  const show = (field: string) => (touched ? errors[field] : undefined);

  const derivation = deriveConversionRate({
    paidAmount: event.paidAmount,
    paidCurrency: event.paidCurrency,
    amount: num(state.amount),
    fxRate: num(state.fxRate),
  });

  const submit = () => {
    setTouched(true);
    if (Object.keys(errors).length > 0) return;
    save.mutate();
  };

  const accountOptions = accountList.data?.items ?? [];

  return (
    <Modal
      open
      onClose={onClose}
      title={editing ? 'Edit leg account' : 'Add conversion leg'}
      description={
        editing
          ? 'Only the account is editable — the leg keeps its amount, date, rate and bills.'
          : `The ₽ side of ${formatAmount(event.paidAmount, event.paidCurrency)} — it makes the ₽ total count.`
      }
    >
      <form
        onSubmit={(formEvent) => {
          formEvent.preventDefault();
          submit();
        }}
      >
        {editing && leg ? (
          <dl className="dl" style={{ marginTop: 0 }}>
            <dt>Leg</dt>
            <dd>
              {formatAmount(leg.amount, leg.currency)} · {formatDateTime(leg.occurredAt)}
              {leg.fxRate !== null ? ` · ${leg.fxRate} ₽/${leg.currency}` : ''}
            </dd>
          </dl>
        ) : (
          <p className="field__hint" style={{ margin: '0 0 var(--space-3)' }}>
            Type <Badge>CONVERSION</Badge> — the rubles the bank charged, and the rate it printed if you have it.
          </p>
        )}

        <div className="form-grid">
          {!editing ? (
            <>
              <Field label="Converted at" htmlFor="conv-at" required error={show('occurredAt')}>
                <Input
                  id="conv-at"
                  type="datetime-local"
                  value={state.occurredAt}
                  aria-invalid={Boolean(show('occurredAt'))}
                  onChange={(changeEvent) => set('occurredAt', changeEvent.target.value)}
                />
              </Field>

              <Field label="Currency" htmlFor="conv-currency" required error={show('currency')}>
                <Input
                  id="conv-currency"
                  maxLength={3}
                  value={state.currency}
                  aria-invalid={Boolean(show('currency'))}
                  onChange={(changeEvent) => set('currency', changeEvent.target.value.toUpperCase())}
                />
              </Field>

              <Field
                label="RUB amount"
                htmlFor="conv-amount"
                required
                error={show('amount')}
                hint={state.fxRate.trim() === '' ? undefined : derivation.hint ?? undefined}
              >
                <Input
                  id="conv-amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={state.amount}
                  aria-invalid={Boolean(show('amount'))}
                  onChange={(changeEvent) => {
                    const value = changeEvent.target.value;
                    set('amount', value);
                    const paid = event.paidAmount;
                    const typed = num(value);
                    // Typing the rubles derives the rate — the reverse direction
                    // of the field below, and always the same formula.
                    if (state.fxRate.trim() === '' && typed !== null && typed > 0 && paid > 0) {
                      set('fxRate', String(Math.round((typed / paid) * 1_000_000) / 1_000_000));
                    }
                  }}
                />
              </Field>

              <Field
                label="FX rate (₽ per unit)"
                htmlFor="conv-fx"
                hint={derivation.hint ?? 'e.g. 83.42 — leave empty to book only the rubles'}
              >
                <Input
                  id="conv-fx"
                  type="number"
                  min="0"
                  step="0.000001"
                  value={state.fxRate}
                  onChange={(changeEvent) => {
                    const value = changeEvent.target.value;
                    set('fxRate', value);
                    const typed = num(value);
                    const derived = deriveConversionRate({
                      paidAmount: event.paidAmount,
                      paidCurrency: event.paidCurrency,
                      amount: null,
                      fxRate: typed,
                    });
                    if (typed !== null && typed > 0 && derived.rubAmount !== null && num(state.amount) === null) {
                      set('amount', String(derived.rubAmount));
                    }
                  }}
                />
              </Field>

              <Field label="Rate source" htmlFor="conv-source" hint="Where the rate came from">
                <Select
                  id="conv-source"
                  value={state.rateSource}
                  disabled={state.fxRate.trim() === ''}
                  onChange={(changeEvent) => set('rateSource', changeEvent.target.value as LegState['rateSource'])}
                >
                  {RATE_SOURCES.map((source) => (
                    <option key={source} value={source}>
                      {source}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field label="Commission" htmlFor="conv-commission">
                <Input
                  id="conv-commission"
                  type="number"
                  min="0"
                  step="0.01"
                  value={state.commission}
                  onChange={(changeEvent) => set('commission', changeEvent.target.value)}
                />
              </Field>
            </>
          ) : null}

          <div className="form-grid__full">
            <Field
              label={editing ? 'Account' : 'Source account'}
              htmlFor="conv-account"
              error={show('accountId')}
              hint={
                editing
                  ? undefined
                  : 'Optional — a leg with no account is attributed from its bill (card tail + currency); without a bill the API asks for one.'
              }
            >
              <Select id="conv-account" value={state.accountId} onChange={(changeEvent) => set('accountId', changeEvent.target.value)}>
                {!editing ? <option value="">— infer from the bill —</option> : null}
                {editing && leg && !accountOptions.some((account) => account.id === leg.accountId) ? (
                  <option value={leg.accountId}>{leg.account?.name ?? leg.accountId}</option>
                ) : null}
                {accountOptions.map((account) => (
                  <option key={account.id} value={account.id}>
                    {account.name} ({account.currency})
                  </option>
                ))}
              </Select>
            </Field>
          </div>

          {!editing ? (
            <div className="form-grid__full">
              <Field label="Receipt №" htmlFor="conv-txnno" hint="The bank's operation number — the dedup key">
                <Input
                  id="conv-txnno"
                  value={state.sourceTxnNo}
                  onChange={(changeEvent) => set('sourceTxnNo', changeEvent.target.value)}
                />
              </Field>
            </div>
          ) : null}
        </div>

        <div className="modal__footer" style={{ padding: 'var(--space-4) 0 0' }}>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" loading={save.isPending}>
            {editing ? 'Save account' : 'Add leg'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
